import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { API } from '../../config/config';
import { useAuth } from '../../hooks/useAuth';
import AddressForm from './AddressForm';
import toast from 'react-hot-toast';
import { useAddressList } from '../../hooks/useAddressList';
import { useOrderManagementStore } from '../../zustandStore/store.ordermgt';
import { useOrderDataStore } from '../../zustandStore/store.order-data';

// Define the Address interface with id for displaying existing addresses
interface Address {
  id: string;
  line1: string;
  line2?: string;
  city?: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

interface AddressListProps {
  buyerEmail?: string;
  title?: string;
  showAddButton?: boolean;
  onSelectEvent?: (addressLength: number) => void;
}

const AddressList: React.FC<AddressListProps> = ({
  buyerEmail = '',
  title = 'Addresses',
  showAddButton = true,
  onSelectEvent
}) => {
  const orderManagementState = useOrderManagementStore((state) => state);
  const orderData = useOrderDataStore((state) => state);
  // Store Actions
  const orderMgtActions = orderManagementState.actions;
  const orderDataActions = orderData.actions;
  const [openAddressForm, setOpenAddressForm] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  const {
    fetchAddresses,
    addresses,
    createAddress,
    deleteAddress,
    loading: isLoading
  } = useAddressList();

  // Initial fetch
  useEffect(() => {
    if (buyerEmail) {
      fetchAddresses(buyerEmail).then();
    }
  }, [buyerEmail]);

  // Handle delete address
  const handleDeleteAddress = async (addressId: string) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    deleteAddress(addressId).then();
  };

  const onSelectAddress = (address: Address) => {
    // Set the selected address for further actions
    setSelectedAddress(address);
    // Set the current address in Zustand store or context if needed
    orderDataActions.setEmail(orderManagementState.settings?.authUser?.email);
    orderDataActions.setAddress(address);
    onSelectEvent && onSelectEvent(addresses.length);
  };

  // Format address for display
  const formatAddress = (address: Address): string => {
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(part => part && part.trim() !== '');

    return parts.join(', ');
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 7
        }}
      >
        <Typography variant="h5" component="h2">
          {title}
        </Typography>

        {showAddButton && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddressForm(true)}
          >
            Add New Address
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : addresses.length === 0 ? (
        <Card sx={{
          mt: '2%'
        }}>
          <CardContent>
            <Typography variant="body1" color="textSecondary" align="center">
              No addresses found. Click "Add New Address" to add one.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {addresses.map((address) => (
            <Grid item xs={12} md={6} key={address.id}>
              <Card
                onClick={(e) => onSelectAddress(address)}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  height:'150px',
                  border:
                    selectedAddress?.id === address.id
                      ? '2px solid #007bff'
                      : '1px solid #ccc',
                  borderColor:
                    selectedAddress?.id === address.id ? '#007bff' : '#ccc',
                  borderWidth:
                    selectedAddress?.id === address.id ? '2px' : '1px',
                  borderStyle:
                    selectedAddress?.id === address.id ? 'solid' : 'solid',
                  borderCollapse: 'collapse',
                  borderSpacing: '0'
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {address.line1}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatAddress(address)}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2">
                        <strong>Phone:</strong> {address.phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Address Form Dialog */}
      <AddressForm
        open={openAddressForm}
        onClose={() => setOpenAddressForm(false)}
        onAddressAdded={() => {
          fetchAddresses(buyerEmail).then();
          setOpenAddressForm(false);
        }}
        buyerEmail={buyerEmail}
      />
    </Box>
  );
};

export default AddressList;
